/**
 * Google Maps API Interceptor
 * Intercepts Google Maps internal API calls and redirects them to our secure proxy
 */

import api from '@/plugins/api';

export const setupGoogleMapsInterceptor = () => {
  // Store original fetch function
  const originalFetch = window.fetch;
  
  // Store original XMLHttpRequest
  const originalXMLHttpRequest = window.XMLHttpRequest;
  
  // Store original createElement function
  const originalCreateElement = document.createElement;

  // Override createElement to intercept script tag creation
  document.createElement = function(tagName: string) {
    const element = originalCreateElement.call(this, tagName);
    
    if (tagName.toLowerCase() === 'script') {
      const originalSetAttribute = element.setAttribute;
      element.setAttribute = function(name: string, value: string) {
        // Block ALL Google Maps API script calls that expose the key
        if (name === 'src' && 
            value.includes('maps.googleapis.com') && 
            (value.includes('key=') || value.includes('&key='))) {
          console.log('🚫 Intercepting Google Maps script tag with exposed key:', value);
          
          try {
            // Extract the API path from the Google URL
            const urlObj = new URL(value);
            const apiPath = urlObj.pathname.replace('/maps/', 'maps/') + urlObj.search;
            
            // Build our proxy URL
            const proxyUrl = `/api/google-maps-proxy/${apiPath}`;
            
            // Remove the key parameter since our backend will add it
            const proxyUrlObj = new URL(proxyUrl, window.location.origin);
            proxyUrlObj.searchParams.delete('key');
            
            console.log('🔄 Redirecting script tag to backend proxy:', proxyUrlObj.toString());
            
            // Use our proxy URL instead
            return originalSetAttribute.call(this, name, proxyUrlObj.toString());
          } catch (error) {
            console.error('Google Maps script interceptor error:', error);
            // Block the script if proxy fails
            return originalSetAttribute.call(this, name, 'about:blank');
          }
        }
        return originalSetAttribute.call(this, name, value);
      };
    }
    
    return element;
  };

  // Override fetch to intercept Google Maps API calls
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input.toString();
    
    // Intercept ALL Google Maps API calls that contain API keys
    if (url.includes('maps.googleapis.com') && (url.includes('key=') || url.includes('&key='))) {
      console.log('🚫 Intercepting Google Maps API call with exposed key:', url);
      try {
        // Extract the API path from the Google URL
        const urlObj = new URL(url);
        const apiPath = urlObj.pathname.replace('/maps/', 'maps/') + urlObj.search;
        
        // Build our proxy URL
        const proxyUrl = `/api/google-maps-proxy/${apiPath}`;
        
        // Remove the key parameter since our backend will add it
        const proxyUrlObj = new URL(proxyUrl, window.location.origin);
        proxyUrlObj.searchParams.delete('key');
        
        console.log('🔄 Redirecting to backend proxy:', proxyUrlObj.toString());
        
        // Call our backend proxy instead
        const response = await originalFetch(proxyUrlObj.toString(), init);
        
        return response;
        
      } catch (error) {
        console.error('Google Maps interceptor error:', error);
        // Fallback to original request if our proxy fails
        return originalFetch(input, init);
      }
    }
    
    // Allow all other requests (including Google Maps calls without API keys)
    return originalFetch(input, init);
  };

  // Override XMLHttpRequest to intercept Google Maps API calls
  window.XMLHttpRequest = function() {
    const xhr = new originalXMLHttpRequest();
    const originalOpen = xhr.open;
    
    xhr.open = function(method: string, url: string | URL, async = true, user?: string | null, password?: string | null) {
      const urlString = typeof url === 'string' ? url : url.toString();
      
      // Intercept Google Maps API calls with API keys
      if (urlString.includes('maps.googleapis.com') && (urlString.includes('key=') || urlString.includes('&key='))) {
        console.log('🚫 Intercepting Google Maps XHR call with exposed key:', urlString);
        
        try {
          // Extract the API path from the Google URL
          const urlObj = new URL(urlString);
          const apiPath = urlObj.pathname.replace('/maps/', 'maps/') + urlObj.search;
          
          // Build our proxy URL
          const proxyUrl = `/api/google-maps-proxy/${apiPath}`;
          
          // Remove the key parameter since our backend will add it
          const proxyUrlObj = new URL(proxyUrl, window.location.origin);
          proxyUrlObj.searchParams.delete('key');
          
          console.log('🔄 Redirecting XHR to backend proxy:', proxyUrlObj.toString());
          
          // Call original open with our proxy URL
          return originalOpen.call(this, method, proxyUrlObj.toString(), async as boolean, user, password);
        } catch (error) {
          console.error('Google Maps XHR interceptor error:', error);
          // Fallback to original request if our proxy fails
          return originalOpen.call(this, method, urlString, async as boolean, user, password);
        }
      }
      
      // Allow all other requests
      return originalOpen.call(this, method, urlString, async as boolean, user, password);
    };
    
    return xhr;
  } as any;
  
  console.log('Google Maps API interceptor initialized - all API calls (fetch & XHR) now go through secure proxy');
};

export const removeGoogleMapsInterceptor = () => {
  // Restore original functions if needed
  // Note: This is complex due to closure scope - better to reload page for cleanup
  console.log('Google Maps interceptor cleanup requested - consider page reload for full cleanup');
};